import { Link } from 'react-router-dom'
import { Botao } from '../../components/Botao'
import { dataHora } from '../../lib/datas'
import { TEXTOS } from '../../lib/glossario'
import type { LinhaPorPessoa } from '../../types/repositorios'

const INTEIRO = new Intl.NumberFormat('pt-BR')

const CELULA = 'px-3 py-2 text-right tabular-nums text-slate-700'

/** Quantas áreas cabem na célula antes de virar "+n". */
export const AREAS_NA_LINHA = 3

interface Props {
  pessoa: LinhaPorPessoa
  competencia: string
  fiscal: boolean
  aoCorrigirSetor: () => void
  aoVerArquivos: () => void
}

/**
 * Uma pessoa na tabela por pessoa.
 *
 * As áreas aparecem como chips, três no máximo: o resto vira "+n" com o nome das demais no
 * `title`, porque a coluna precisa caber ao lado dos números sem quebrar a linha em três. O
 * `title` só existe para quem passa o mouse; o nome das demais vai também num texto só para
 * leitor de tela, senão quem navega por teclado ou por voz nunca fica sabendo quais são.
 *
 * @param props a pessoa, o mês, se quem vê é fiscal e as duas ações
 * @returns a linha
 */
export function LinhaDaPessoa({ pessoa: p, competencia, fiscal, aoCorrigirSetor, aoVerArquivos }: Props) {
  const t = TEXTOS.repositorios
  const areasVisiveis = p.areas.slice(0, AREAS_NA_LINHA)
  const areasOcultas = p.areas.slice(AREAS_NA_LINHA)

  return (
    <tr className="border-b border-slate-100 last:border-0">
      <td className="px-3 py-2">
        <p className="font-medium text-slate-900">{p.nome}</p>
        <p className="text-xs text-slate-500">
          {p.email}
          {p.login && ` · @${p.login}`}
          {p.setorFixadoPorPessoa && <span className="ml-1 text-niteroi-700"> · {t.setorFixado}</span>}
        </p>
      </td>
      <td className={`${CELULA} font-semibold text-slate-900`}>{INTEIRO.format(p.commits)}</td>
      <td className={CELULA}>{INTEIRO.format(p.comSolicitacaoCitada)}</td>
      <td className={CELULA}>{INTEIRO.format(p.integracoes)}</td>
      <td className={CELULA}>{INTEIRO.format(p.diasAtivos)}</td>
      <td className={CELULA}>{INTEIRO.format(p.repositorios)}</td>
      <td className={CELULA}>
        +{INTEIRO.format(p.adicoes)} / −{INTEIRO.format(p.remocoes)}
      </td>
      <td className="px-3 py-2">
        <span className="flex flex-wrap gap-1">
          {areasVisiveis.map((area) => (
            <span key={area} className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-700">
              {area}
            </span>
          ))}
          {areasOcultas.length > 0 && (
            <span className="rounded px-1 py-0.5 text-xs text-slate-500" title={areasOcultas.join(', ')}>
              <span aria-hidden="true">{t.pessoas.maisAreas(areasOcultas.length)}</span>
              <span className="sr-only">{t.pessoas.outrasAreas(areasOcultas)}</span>
            </span>
          )}
        </span>
      </td>
      <td className="px-3 py-2 text-xs text-slate-600">{dataHora(p.ultimoCommitEm)}</td>
      <td className="px-3 py-2">
        <div className="flex flex-wrap justify-end gap-2">
          <Link
            to={`/repositorios?competencia=${competencia}&aba=commits&autorId=${p.autorId}`}
            className="text-xs font-medium text-niteroi-700 underline underline-offset-2 hover:text-niteroi-900"
          >
            {t.pessoas.verCommits}
          </Link>
          <Botao aparencia="texto" tamanho="pequeno" onClick={aoVerArquivos}>
            {t.pessoas.verArquivos}
          </Botao>
          {fiscal && (
            <Botao aparencia="texto" tamanho="pequeno" onClick={aoCorrigirSetor}>
              {t.pessoas.corrigirSetor}
            </Botao>
          )}
        </div>
      </td>
    </tr>
  )
}
